import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { fetchData, api } from "../helper/api.interceptor";
import indiaLocations from "../data/indiaLocations.json";
import "../electricityprovider/electricityProvider.scss";

const AddUserModal = ({ show, onClose, onUserAdded }) => {
    const navigate = useNavigate();

    const [form, setForm] = useState({
        username: "",
        emailid: "",
        phone_number: "+91 ",
        roles: [],
        warehouse_id: "",
        state: "",
        city: "",
        street: "",
        pincode: "",
    });

    const [cities, setCities] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [loading, setLoading] = useState(false);

    const roleOptions = [
        { label: "Sales Executive", value: "Sales Executive" },
        { label: "Inventory Manager", value: "Inventory Manager" },
        { label: "Onsite Executive", value: "Onsite Executive" },
        { label: "File Manager", value: "File Manager" },
    ];

    /* ------------------------ FETCH WAREHOUSE ------------------------ */
    useEffect(() => {
        if (show) fetchWarehouses();
    }, [show]);

    const fetchWarehouses = async () => {
        try {
            const response = await fetchData.get("/get-all-warehouse");
            if (response?.status_code === 200) {
                setWarehouses(response.result || []);
            }
        } catch (err) {
            console.error("Warehouse fetch error:", err);
        }
    };

    /* ------------------------ LOAD CITIES WHEN STATE CHANGES ------------------------ */
    useEffect(() => {
        if (form.state) {
            const stateObj = indiaLocations.find((s) => s.state === form.state);
            setCities(stateObj?.districts?.map((d) => d.district) || []);
        } else {
            setCities([]);
        }
    }, [form.state]);

    /* ------------------------ HANDLE INPUT CHANGE ------------------------ */
    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "phone_number") {
            let cleaned = value.replace(/^\+91\s?/, "").replace(/\D/g, "");
            setForm((prev) => ({ ...prev, phone_number: `+91 ${cleaned}` }));
        } else {
            setForm((prev) => ({ ...prev, [name]: value }));
        }
    };

    /* ------------------------ AUTO-LOAD USER ON PHONE INPUT ------------------------ */
    const handlePhoneInput = async (e) => {
        let cleaned = e.target.value.replace(/^\+91\s?/, "").replace(/\D/g, "");

        setForm((prev) => ({ ...prev, phone_number: `+91 ${cleaned}` }));

        if (cleaned.length === 10) {
            const fullPhone = `+91 ${cleaned}`;
            try {
                const res = await api.getUserByPhone(fullPhone);

                if (res?.status_code === 200 && res?.result) {
                    const user = res.result;

                    setForm({
                        username: user.username || "",
                        emailid: user.emailid || "",
                        phone_number: fullPhone,
                        roles: user.roles || [],
                        warehouse_id: user.warehouse_id || "",
                        state: user.address?.state || "",
                        city: user.address?.city || "",
                        street: user.address?.street || "",
                        pincode: user.address?.pincode || "",
                    });

                    Swal.fire("Found", "Existing user data loaded", "success");
                }
            } catch (err) {
                console.log("User not found");
            }
        }
    };

    /* ------------------------ SUBMIT FORM ------------------------ */
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (
            !form.username ||
            !form.emailid ||
            !form.phone_number ||
            !form.roles.length ||
            !form.state ||
            !form.city
        ) {
            return Swal.fire("Missing Fields", "Please fill out all required fields", "warning");
        }

        if (!form.warehouse_id) {
            return Swal.fire(
                "Missing Warehouse",
                "Warehouse is required",
                "warning"
            );
        }

        setLoading(true);
        try {
            const formData = new FormData();
            Object.keys(form).forEach((key) => {
                if (key === "roles") {
                    formData.append("roles", form.roles.join(","));
                } else {
                    formData.append(key, form[key]);
                }
            });

            const response = await fetchData.postWithFormData("/user/add-user", formData);

            if (response?.status_code === 200) {
                Swal.fire("Success", "User added successfully", "success");
                onUserAdded();
                resetForm();
                onClose();
            } else {
                throw new Error(response?.message || "Failed to add user");
            }
        } catch (err) {
            Swal.fire("Error", err.message, "error");
        } finally {
            setLoading(false);
        }
    };

    /* ------------------------ RESET FORM ------------------------ */
    const resetForm = () => {
        setForm({
            username: "",
            emailid: "",
            phone_number: "+91 ",
            roles: [],
            warehouse_id: "",
            state: "",
            city: "",
            street: "",
            pincode: "",
        });
        setCities([]);
    };

    if (!show) return null;

    return (
        <div className="modal-backdrop">
            <div className="modal-container">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Add User</h5>
                        <button type="button" className="close-btn" onClick={onClose}>
                            <Icon icon="mdi:close" />
                        </button>
                    </div>

                    <div className="modal-body">
                        <form onSubmit={handleSubmit} className="form-2columns">

                            {/* PHONE */}
                            <div className="form-group">
                                <label>Phone Number *</label>
                                <input
                                    type="text"
                                    name="phone_number"
                                    value={form.phone_number}
                                    onChange={handlePhoneInput}
                                    className="form-control"
                                    placeholder="Enter phone number"
                                    required
                                />
                            </div>

                            {/* USERNAME */}
                            <div className="form-group">
                                <label>Username *</label>
                                <input
                                    type="text"
                                    name="username"
                                    value={form.username}
                                    onChange={handleChange}
                                    className="form-control"
                                    placeholder="Enter username"
                                    required
                                />
                            </div>

                            {/* EMAIL */}
                            <div className="form-group">
                                <label>Email *</label>
                                <input
                                    type="email"
                                    name="emailid"
                                    value={form.emailid}
                                    onChange={handleChange}
                                    className="form-control"
                                    placeholder="Enter email"
                                    required
                                />
                            </div>

                            {/* ROLES */}
                            <div className="form-group">
                                <label>Roles *</label>
                                <Select
                                    options={roleOptions}
                                    isMulti
                                    value={form.roles.map((r) => ({ label: r, value: r }))}
                                    onChange={(selected) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            roles: selected ? selected.map((s) => s.value) : [],
                                        }))
                                    }
                                    placeholder="Select one or more roles"
                                />
                            </div>

                            {/* WAREHOUSE */}
                            <div className="form-group">
                                <label>Warehouse *</label>
                                <Select
                                    options={[
                                        { label: "+ Add Warehouse", value: "add_warehouse" },
                                        ...warehouses.map((w) => ({
                                            label: `${w.warehouse_name} (${w.warehouse_id})`,
                                            value: w.warehouse_id,
                                        })),
                                    ]}
                                    value={
                                        form.warehouse_id
                                            ? {
                                                label:
                                                    warehouses.find(
                                                        (w) => w.warehouse_id === form.warehouse_id
                                                    )?.warehouse_name || form.warehouse_id,
                                                value: form.warehouse_id,
                                            }
                                            : null
                                    }
                                    onChange={(selected) => {
                                        if (selected?.value === "add_warehouse") {
                                            onClose();
                                            navigate("/warehouse-details");
                                        } else {
                                            setForm((prev) => ({
                                                ...prev,
                                                warehouse_id: selected?.value || "",
                                            }));
                                        }
                                    }}
                                    placeholder="Search & Select Warehouse"
                                    isClearable
                                />
                            </div>

                            {/* STATE */}
                            <div className="form-group">
                                <label>State *</label>
                                <Select
                                    options={indiaLocations.map((s) => ({
                                        label: s.state,
                                        value: s.state,
                                    }))}
                                    value={
                                        form.state ? { label: form.state, value: form.state } : null
                                    }
                                    onChange={(selected) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            state: selected?.value || "",
                                        }))
                                    }
                                    placeholder="Search & Select State"
                                    isClearable
                                />
                            </div>

                            {/* CITY */}
                            <div className="form-group">
                                <label>City *</label>
                                <Select
                                    options={cities.map((c) => ({
                                        label: c,
                                        value: c,
                                    }))}
                                    value={
                                        form.city
                                            ? { label: form.city, value: form.city }
                                            : null
                                    }
                                    onChange={(selected) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            city: selected?.value || "",
                                        }))
                                    }
                                    placeholder="Search & Select City"
                                    isDisabled={!cities.length}
                                    isClearable
                                />
                            </div>

                            {/* STREET */}
                            <div className="form-group">
                                <label>Street</label>
                                <input
                                    type="text"
                                    name="street"
                                    value={form.street}
                                    onChange={handleChange}
                                    className="form-control"
                                    placeholder="Enter street"
                                />
                            </div>

                            {/* PINCODE */}
                            <div className="form-group">
                                <label>Pincode</label>
                                <input
                                    type="text"
                                    name="pincode"
                                    value={form.pincode}
                                    onChange={handleChange}
                                    className="form-control"
                                    placeholder="Enter pincode"
                                />
                            </div>
                        </form>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Close
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <Icon icon="eos-icons:loading" className="spinner" /> Adding...
                                </>
                            ) : (
                                "Add User"
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddUserModal;
